import axios from "axios";
import { API_BASE_URL } from "utils/api";
import { getToken } from "utils/auth";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Fetch a single client's details
 * @param {string} clientId - The ID of the client to fetch
 * @returns {Promise} - Response from the API containing client details
 * @throws {Error} - If the fetch fails
 */
export const getClient = async (clientId) => {
  try {
    const url = `/clients/${encodeURIComponent(clientId)}`;
    const response = await api.get(url);

    if (response?.data?.status === false) {
      throw new Error(response?.data?.message || "Failed to fetch client");
    }

    return response?.data;
  } catch (error) {
    console.error("Error in getClient:", error.message, error.response?.data);
    throw error;
  }
};

/**
 * Update a client with new data
 * @param {string} clientId - The ID of the client to update
 * @param {FormData} formData - FormData object containing client fields and profile image
 * @returns {Promise} - Response from the API
 * @throws {Error} - If the update fails
 */
export const updateClient = async (clientId, formData) => {
  try {
    const response = await api.put(
      `/clients/${encodeURIComponent(clientId)}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response?.data?.status === false) {
      throw new Error(response?.data?.message || "Failed to update client");
    }

    return response?.data;
  } catch (error) {
    // Improve error message from backend validation
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      const errorMessages = error.response.data.errors
        .map((err) => err.msg || err.message)
        .join(", ");
      throw new Error(errorMessages || error.message);
    }

    throw error;
  }
};

/**
 * Add a schedule to a client
 * @param {string} clientId - The ID of the client
 * @param {object} scheduleData - Schedule object with scheduledTime, note, scheduleStatus
 * @returns {Promise} - Response from the API
 * @throws {Error} - If the request fails
 */
export const addClientSchedule = async (clientId, scheduleData) => {
  try {
    const response = await api.post("/clients/add-schedule", {
      clientId,
      ...scheduleData,
    });

    if (response?.data?.status === false) {
      throw new Error(response?.data?.message || "Failed to add schedule");
    }

    return response?.data;
  } catch (error) {
    console.error("Error in addClientSchedule:", error.message, error.response?.data);
    throw error;
  }
};

/**
 * Edit a single client schedule
 * Uses PUT /clients/edit-schedule
 */
export const editClientSchedule = async (clientId, scheduleId, scheduleData) => {
  try {
    const response = await api.put("/clients/edit-schedule", {
      clientId,
      scheduleId,
      ...scheduleData,
    });

    if (response?.data?.status === false) {
      throw new Error(response?.data?.message || "Failed to edit schedule");
    }

    return response?.data;
  } catch (error) {
    console.error("Error in editClientSchedule:", error.message, error.response?.data);
    throw error;
  }
};

/**
 * Delete a single client schedule
 */
export const deleteClientSchedule = async (clientId, scheduleId) => {
  try {
    const response = await api.delete(
      `/clients/${encodeURIComponent(clientId)}/schedule/${encodeURIComponent(scheduleId)}`
    );

    if (response?.data?.status === false) {
      throw new Error(response?.data?.message || "Failed to delete schedule");
    }

    return response?.data;
  } catch (error) {
    console.error("Error in deleteClientSchedule:", error.message, error.response?.data);
    throw error;
  }
};

export const addClientInstallment = async (clientId, payload) => {
  try {
    const response = await api.post(`/clients/${encodeURIComponent(clientId)}/installments`, payload);
    return response?.data;
  } catch (error) {
    console.error("Error in addClientInstallment:", error.message, error.response?.data);
    throw error;
  }
};


